import React, { useRef } from "react";
import { Film } from "lucide-react";
import { estimateFormRating } from "@/lib/captureRating";
import { scoreColor, severityColors, calloutColor } from "@/lib/captureTheme";
import RepSparkline from "@/components/capture/RepSparkline";

/**
 * Read-only capture analysis.
 *
 * Rendered in three places from one implementation:
 *   • the coach's detail page (which wraps its own editing controls around it)
 *   • a student's own capture view
 *   • a public share link (/c/{token})
 *
 * Two rules make it safe to share the SAME renderer with the outside world:
 *
 *  1. It never reads `coach_rating` / `coach_rating_note`. Those are the coach's
 *     private calibration override, used to tune the rating formula — not a
 *     coaching output. The rating shown here is recomputed from `pose_summary`,
 *     so it appears on a shared link without leaking the override.
 *  2. It renders nothing that mutates: no override editor, no delete, no tie
 *     controls. A public page cannot inherit a coach-only button by accident.
 *
 * @param {object}  analysis          public payload OR authed session document
 * @param {string?} clipSrc           clip URL, or null when there's no video
 * @param {string?} clipDownloadName  filename offered on download, if any
 * @param {string?} footnote          e.g. "Shared by Coach X"
 */
export default function CaptureAnalysis({
  analysis,
  clipSrc = null,
  clipDownloadName = "capture-clip.mp4",
  footnote = null,
}) {
  const videoElRef = useRef(null);

  if (!analysis) return null;

  const sum = analysis.pose_summary || {};
  const ai = analysis.ai_analysis || {};
  const pb = sum.pickleball_stats || {};
  const strokes = pb.stroke_breakdown || {};
  const ready = pb.ready_position || {};
  const reps = sum.reps || [];
  const keyframes = sum.keyframes || {};

  const formScore = Math.round(sum.form_score ?? sum.overall_score ?? 0);
  const consistency = sum.consistency != null ? Math.round(sum.consistency * 100) : null;
  const playMode = analysis.play_mode || "drill";
  const formRating = estimateFormRating({ ...sum, play_mode: playMode }, playMode);
  const severityColor = severityColors();

  const seekTo = (t) => {
    const v = videoElRef.current;
    if (!v || t == null || !Number.isFinite(t)) return;
    try {
      v.currentTime = Math.max(0, t);
      v.play().catch(() => {});
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-6" data-testid="capture-analysis">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#38BDF8] font-bold">
          Video analysis
        </div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter mt-2">
          {analysis.student_name || "Athlete"}
        </h1>
        <div className="text-sm text-[#A3A3A3] mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {analysis.created_at && <span>{new Date(analysis.created_at).toLocaleString()}</span>}
          <span className="uppercase tracking-widest text-[10px] font-display font-bold text-[#38BDF8]">
            {playMode}
          </span>
          {analysis.mode && (
            <span className="uppercase tracking-widest text-[10px] font-display font-bold text-[#A3A3A3]">
              {analysis.mode}
            </span>
          )}
        </div>
        {analysis.notes && (
          <p className="text-sm text-white/80 mt-3 border-l-2 border-[#38BDF8]/60 pl-3">
            {analysis.notes}
          </p>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        {/* LEFT RAIL */}
        <div className="space-y-4">
          <div className="card-surface p-6">
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold">
              Form score
            </div>
            <div
              className="font-display font-black text-8xl mt-2 leading-none tabular-nums"
              style={{ color: scoreColor(formScore) }}
              data-testid="capture-form-score"
            >
              {formScore || "—"}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <Stat label="Reps" value={sum.rep_count ?? reps.length ?? 0} />
              <Stat label="Duration" value={`${Math.round(analysis.duration_seconds || 0)}s`} />
              <Stat label="Consistency" value={consistency != null ? `${consistency}%` : "—"} />
              <Stat
                label="Detection"
                value={sum.detection_rate != null ? `${Math.round(sum.detection_rate * 100)}%` : "—"}
              />
            </div>

            {formRating && (
              <div className="mt-6 border-t border-white/10 pt-4" data-testid="form-rating-card">
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold mb-2">
                  Form-estimated rating
                </div>
                <div className="flex items-baseline gap-2">
                  <div
                    className="font-display font-black text-5xl tabular-nums leading-none"
                    style={{ color: scoreColor((formRating.rating ?? 0) * 20) }}
                    data-testid="form-rating-value"
                  >
                    ~{formRating.rating.toFixed(1)}
                  </div>
                  <div className="text-[11px] text-[#A3A3A3] font-mono">
                    ± {formRating.band.toFixed(1)}
                  </div>
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold">
                  {formRating.confidence} confidence · {formRating.reps} reps
                </div>
                {formRating.context && (
                  <div
                    className="mt-2 text-[11px] text-white/75 border-l-2 border-[#38BDF8]/60 pl-2 leading-relaxed"
                    data-testid="form-rating-context"
                  >
                    {formRating.context}
                  </div>
                )}
              </div>
            )}

            {/* Per-rep sparkline */}
            {reps.length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold mb-2">
                  Per-rep score
                </div>
                <RepSparkline reps={reps} clipSrc={clipSrc} onSeek={seekTo} />
              </div>
            )}

            {/* Stroke breakdown */}
            {Object.keys(strokes).length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold mb-2">
                  Stroke breakdown
                </div>
                <div className="space-y-1.5">
                  {Object.entries(strokes).map(([type, data]) => (
                    <div key={type} className="flex items-center gap-3 text-sm" data-testid={`stroke-row-${type}`}>
                      <span className="font-display uppercase tracking-widest text-xs font-bold text-white w-16 flex-shrink-0">
                        {type}
                      </span>
                      <span className="text-[#A3A3A3] font-mono text-xs w-10 text-center">
                        {data.count}×
                      </span>
                      <div className="flex-1 h-2 bg-white/5 relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 transition-all"
                          style={{ width: `${data.avg_score || 0}%`, background: scoreColor(data.avg_score) }}
                        />
                      </div>
                      <span className="text-xs font-mono text-white w-8 text-right">{data.avg_score || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ready position */}
            {ready && ready.ready_score != null && (
              <div className="mt-6 border-t border-white/10 pt-4" data-testid="capture-ready-position">
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold mb-2">
                  Ready position · between shots
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <ReadyCell value={ready.ready_score} color={scoreColor(ready.ready_score)} label="Ready score" />
                  <ReadyCell value={`${ready.paddle_up_pct ?? "—"}%`} label="Paddle up" />
                  <ReadyCell value={ready.knee_bend_avg ? `${ready.knee_bend_avg}°` : "—"} label="Knee bend" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT RAIL */}
        <div className="space-y-6">
          {clipSrc && (
            <section className="card-surface p-3" data-testid="capture-clip-player">
              <div className="bg-black flex items-center justify-center" style={{ maxHeight: "75vh", overflow: "hidden" }}>
                <video
                  ref={videoElRef}
                  src={clipSrc}
                  controls
                  preload="metadata"
                  playsInline
                  className="max-h-[75vh] w-auto max-w-full"
                />
              </div>
              <div className="flex items-center justify-between mt-2 px-1">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1">
                  <Film size={11} /> Saved clip
                </div>
                <a
                  href={clipSrc}
                  download={clipDownloadName}
                  className="text-[10px] uppercase tracking-widest text-[#38BDF8] hover:underline"
                  data-testid="capture-clip-download"
                >
                  Download
                </a>
              </div>
            </section>
          )}

          {ai.summary && (
            <section className="card-surface p-6" data-testid="ai-summary">
              <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-display font-bold">AI analysis</div>
              <h2 className="mt-1 font-display font-black uppercase tracking-tight text-2xl">Biomechanics report</h2>
              <p className="mt-4 text-sm text-white/85 leading-relaxed whitespace-pre-line">{ai.summary}</p>
              {ai.elite_comparison && (
                <div className="mt-5 border-l-2 border-[#38BDF8] pl-4 text-sm text-white/75 italic">
                  {ai.elite_comparison}
                </div>
              )}
              {ai.next_focus && (
                <div className="mt-5 bg-black/40 border border-white/10 p-4" data-testid="ai-next-focus">
                  <div className="text-[10px] uppercase tracking-widest text-[#ffab00] font-display font-bold">Next focus</div>
                  <p className="mt-1 text-sm">{ai.next_focus}</p>
                </div>
              )}
            </section>
          )}

          {(keyframes.best_rep || keyframes.worst_rep) && (
            <section className="card-surface p-6" data-testid="visual-evidence">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Visual evidence</h3>
              <p className="text-xs text-[#A3A3A3] mt-1">Skeleton frame at the apex of the best vs worst rep.</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {keyframes.best_rep && <KeyframeCard label="Best rep" accent="#00ff88" data={keyframes.best_rep} />}
                {keyframes.worst_rep && <KeyframeCard label="Worst rep" accent="#ff3b30" data={keyframes.worst_rep} />}
              </div>
            </section>
          )}

          {ai.rep_callouts && ai.rep_callouts.length > 0 && (
            <section className="card-surface p-6" data-testid="rep-callouts">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Rep callouts</h3>
              <ul className="mt-3 space-y-2">
                {ai.rep_callouts.map((c, i) => {
                  // Callouts are indexed by rep; map back to a real rep to seek.
                  const target = reps.find((r) => (r.index ?? -1) === c.rep_index);
                  const t = target?.time_s;
                  return (
                    <li key={i} className="flex items-start gap-3 text-sm" data-testid={`rep-callout-${i}`}>
                      <button
                        type="button"
                        onClick={() => t != null && seekTo(t)}
                        disabled={t == null || !clipSrc}
                        className={`font-display font-black w-12 text-right ${clipSrc && t != null ? "hover:underline" : ""}`}
                        style={{ color: calloutColor(c.label) }}
                        title={t != null && clipSrc ? `Jump to ${t.toFixed(1)}s` : ""}
                      >
                        #{c.rep_index}
                      </button>
                      <span className="text-white/85 flex-1">
                        <span className="text-[10px] uppercase tracking-widest font-display font-bold text-[#A3A3A3] mr-2">
                          {c.label}
                        </span>
                        {c.comment}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {ai.strengths && ai.strengths.length > 0 && (
            <section className="card-surface p-6" data-testid="strengths">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Strengths</h3>
              <ul className="mt-3 space-y-2">
                {ai.strengths.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="text-[#00ff88] font-mono">+</span>
                    <span className="text-white/85">{s}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {ai.improvements && ai.improvements.length > 0 && (
            <section className="card-surface p-6" data-testid="improvements">
              <h3 className="font-display font-bold uppercase tracking-tight text-lg">Improvements</h3>
              <div className="mt-3 space-y-3">
                {ai.improvements.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`improvement-${i}`}
                    className="border border-white/5 p-4"
                    style={{ borderLeftColor: severityColor[it.severity] || "#52525b", borderLeftWidth: 3 }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display font-bold uppercase tracking-tight">{it.area}</span>
                      <span
                        className="text-[10px] uppercase tracking-widest font-display font-bold"
                        style={{ color: severityColor[it.severity] || "#a1a1aa" }}
                      >
                        {it.severity}
                      </span>
                    </div>
                    <p className="text-sm text-[#A3A3A3] mt-1">{it.issue}</p>
                    <p className="text-sm text-white mt-2">
                      <span className="text-[#00ff88] font-mono">→ </span>
                      {it.fix}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!ai.summary && !ai.improvements?.length && !ai.strengths?.length && (
            <div className="card-surface p-8 text-center text-sm text-[#A3A3A3]">
              No AI analysis was generated for this session — usually a sign the tracker couldn't see
              enough of the body (better lighting and framing help) or the AI key isn't configured.
            </div>
          )}

          {footnote && (
            <div className="text-[11px] text-[#A3A3A3] text-center pb-4" data-testid="capture-analysis-footnote">
              {footnote}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-display font-bold">{label}</div>
      <div className="font-display font-bold mt-1">{value}</div>
    </div>
  );
}

function ReadyCell({ value, color, label }) {
  return (
    <div>
      <div className="font-display font-black text-3xl" style={{ color: color || "#fff" }}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-[#A3A3A3] mt-1">{label}</div>
    </div>
  );
}

function KeyframeCard({ label, accent, data }) {
  return (
    <div className="border border-white/10 bg-black/40">
      {data.image && (
        <img
          src={data.image}
          alt={label}
          data-testid={`keyframe-${label.toLowerCase().replace(/\s/g, "-")}`}
          className="w-full max-h-80 object-contain bg-black"
        />
      )}
      <div className="p-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-display font-bold" style={{ color: accent }}>
            {label}
          </div>
          <div className="text-xs text-[#A3A3A3] font-mono mt-0.5">
            t = {typeof data.time_s === "number" ? data.time_s.toFixed(1) : data.time_s}s
          </div>
        </div>
        <div className="font-display font-black text-3xl" style={{ color: accent }}>
          {data.score ?? "—"}
        </div>
      </div>
    </div>
  );
}
